import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../../services/api';
import PerformanceDateComparison from './PerformanceDateComparison';
import PerformanceCoverage from './PerformanceCoverage';

// Convert 'YYYY-MM' (month input) to the month-end 'YYYY-MM-DD'
const monthToMonthEnd = (ym) => {
  if (!ym) return null;
  const [year, month] = ym.split('-').map(Number);
  const lastDay = new Date(year, month, 0).getDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
};

const formatDisplayDate = (iso) => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
};

const PerformanceDateSelector = () => {
  const [asOfDate, setAsOfDate] = useState(null);
  const [latestAvailable, setLatestAvailable] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [view, setView] = useState('asof');   // 'asof' | 'coverage'

  useEffect(() => {
    fetchState();
  }, []);

  const fetchState = async () => {
    setIsLoading(true);
    try {
      const data = await adminAPI.getPerformanceAsOfDate();
      setAsOfDate(data.as_of_date);
      setLatestAvailable(data.latest_available_date);
      setUpdatedAt(data.updated_at);
      setUpdatedBy(data.updated_by);
      setSelectedMonth(data.as_of_date ? data.as_of_date.slice(0, 7) : '');
    } catch (err) {
      console.error('Error fetching performance as-of date:', err);
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to load performance as-of date' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedMonth) {
      setMessage({ type: 'error', text: 'Select a month first, or use "Clear" to revert to latest available' });
      return;
    }
    setSaving(true);
    try {
      const result = await adminAPI.setPerformanceAsOfDate(monthToMonthEnd(selectedMonth));
      setMessage({ type: 'success', text: `Performance as-of date set to ${formatDisplayDate(result.as_of_date)}` });
      fetchState();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save performance as-of date' });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await adminAPI.setPerformanceAsOfDate(null);
      setMessage({ type: 'success', text: 'Cap cleared — performance displays now show the latest available data' });
      fetchState();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to clear performance as-of date' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">Performance Date Selection</h5>
      </div>
      <div className="card-body pb-0">
        <ul className="nav nav-tabs perfdate-tabs">
          {[['asof', 'As-of date'], ['coverage', 'Data coverage']].map(([k, label]) => (
            <li className="nav-item" key={k}>
              <button type="button" className={`nav-link ${view === k ? 'active' : ''}`} onClick={() => setView(k)}>
                {label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {view === 'coverage' && (
        <div className="card-body">
          <PerformanceCoverage />
        </div>
      )}

      {view === 'asof' && (<>
      <div className="card-body">
        <p className="text-muted small mb-3">
          Sets the portal-wide performance as-of date. Performance shown to clients across the
          Dashboard, Factsheets and Portfolio Lab is capped at this month-end — use it when the
          latest month is not yet ready to publish. Clearing the date reverts to the latest
          available audited data. First Choice portfolios remain additionally aligned to the
          Strategic Series reference date. The Portfolio Reports quarterly performance date is
          managed separately and is not affected.
        </p>

        {message && (
          <div className={`alert alert-${message.type === 'error' ? 'danger' : 'success'} alert-dismissible fade show`}>
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm" role="status"></div>
            <span className="ms-2">Loading...</span>
          </div>
        ) : (
          <>
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small">Current setting</div>
                  <div className="fs-5 fw-semibold">
                    {asOfDate ? formatDisplayDate(asOfDate) : 'Latest available (no cap)'}
                  </div>
                  {asOfDate && updatedBy && (
                    <div className="text-muted small mt-1">
                      Last changed by {updatedBy}
                      {updatedAt ? ` on ${new Date(updatedAt).toLocaleDateString('en-AU')}` : ''}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small">Latest available audited date</div>
                  <div className="fs-5 fw-semibold">
                    {latestAvailable ? formatDisplayDate(latestAvailable) : '—'}
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-1">Report performance as at (month-end)</label>
                <input
                  type="month"
                  className="form-control form-control-sm"
                  value={selectedMonth}
                  max={latestAvailable ? latestAvailable.slice(0, 7) : undefined}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                />
              </div>
              <div className="col-auto">
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: 'var(--primary-color)', color: '#fff' }}
                  onClick={handleSave}
                  disabled={saving || !selectedMonth}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
              <div className="col-auto">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleClear}
                  disabled={saving || !asOfDate}
                >
                  Clear — use latest available
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {!isLoading && (
        <div className="card-body border-top">
          <PerformanceDateComparison
            currentAsOfDate={asOfDate}
            latestAvailable={latestAvailable}
          />
        </div>
      )}
      </>)}
    </div>
  );
};

export default PerformanceDateSelector;
